import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const BalanceSheet = () => {
    const [loading, setLoading] = useState(false);
    const [date, setDate] = useState(new Date());
    const [balanceSheet, setBalanceSheet] = useState(null);
    const [comparePeriod, setComparePeriod] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [glEntries, setGlEntries] = useState([]);
    const toast = useRef(null);

    const facilityId = "68354b430bfa0b7ac72078c5"; // Your facility ID

    const compareOptions = [
        { label: 'No Comparison', value: null },
        { label: 'Previous Month', value: 'month' },
        { label: 'Previous Quarter', value: 'quarter' },
        { label: 'Previous Year', value: 'year' }
    ];

    useEffect(() => {
        fetchBalanceSheet();
    }, [date, comparePeriod]);

    const fetchBalanceSheet = async () => {
        try {
            setLoading(true);

            // Fetch accounts and GL entries
            const accountsData = await fetchAccounts();
            const entriesData = await fetchGLEntries();

            // Process the data to create balance sheet
            processBalanceSheetData(accountsData, entriesData);

        } catch (error) {
            console.error('Error fetching balance sheet data:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch balance sheet data: ' + (error.message || 'Unknown error')
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchAccounts = async () => {
        try {
            const response = await makeRequest2(`/api/v1/gl-accounts/${facilityId}`, 'GET');

            let fetchedAccounts = [];
            if (response?.data?.data) {
                fetchedAccounts = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
            } else if (response?.data) {
                fetchedAccounts = Array.isArray(response.data) ? response.data : [response.data];
            } else if (Array.isArray(response)) {
                fetchedAccounts = response;
            }

            console.log('Fetched accounts:', fetchedAccounts);
            setAccounts(fetchedAccounts);
            return fetchedAccounts;

        } catch (error) {
            console.error('Error fetching accounts:', error);
            throw error;
        }
    };

    const fetchGLEntries = async () => {
        try {
            // For balance sheet, we need all entries up to the selected date
            const queryParams = new URLSearchParams();
            queryParams.append('endDate', date.toISOString());

            const endpoint = `/api/v1/gl-entries/get-all-entries/${facilityId}?${queryParams.toString()}`;
            const response = await makeRequest2(endpoint, 'GET');

            let fetchedEntries = [];
            if (response?.data?.data) {
                fetchedEntries = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
            } else if (response?.data) {
                fetchedEntries = Array.isArray(response.data) ? response.data : [response.data];
            } else if (Array.isArray(response)) {
                fetchedEntries = response;
            }

            console.log('Fetched GL entries:', fetchedEntries);
            setGlEntries(fetchedEntries);
            return fetchedEntries;

        } catch (error) {
            console.error('Error fetching GL entries:', error);
            throw error;
        }
    };

    const processBalanceSheetData = (accountsData, entriesData) => {
        if (!accountsData) {
            setBalanceSheet(null);
            return;
        }

        // Group entries by account
        const accountEntries = {};
        if (entriesData && entriesData.length > 0) {
            entriesData.forEach(entry => {
                const { accountId, amount, entryType } = entry;
                if (!accountEntries[accountId]) {
                    accountEntries[accountId] = { debit: 0, credit: 0 };
                }

                if (entryType === 'debit') {
                    accountEntries[accountId].debit += amount;
                } else if (entryType === 'credit') {
                    accountEntries[accountId].credit += amount;
                }
            });
        }

        // Separate accounts by type
        const assets = [];
        const liabilities = [];
        const equity = [];

        accountsData.forEach(account => {
            const accountType = account.accountType?.toLowerCase();
            const accountId = account._id;
            const entries = accountEntries[accountId] || { debit: 0, credit: 0 };

            // Calculate account balance (opening balance + movements)
            let accountBalance = account.accountBalance || 0;
            let netMovement = 0;

            // Calculate net movement based on account type
            if (accountType === 'asset') {
                // Assets: debit increases, credit decreases
                netMovement = entries.debit - entries.credit;
            } else if (accountType === 'liability' || accountType === 'equity') {
                // Liabilities and Equity: credit increases, debit decreases
                netMovement = entries.credit - entries.debit;
            }

            // Final balance = opening balance + net movement
            const finalBalance = accountBalance + netMovement;

            // Only include accounts with non-zero balances
            if (Math.abs(finalBalance) > 0.01) {
                const accountData = {
                    code: account.accountCode,
                    name: account.accountName,
                    amount: finalBalance,
                    accountType: accountType,
                    openingBalance: accountBalance,
                    netMovement: netMovement,
                    debit: entries.debit,
                    credit: entries.credit
                };

                if (accountType === 'asset') {
                    assets.push(accountData);
                } else if (accountType === 'liability') {
                    liabilities.push(accountData);
                } else if (accountType === 'equity') {
                    equity.push(accountData);
                }
            }
        });

        // Calculate totals
        const totalAssets = assets.reduce((sum, item) => sum + Math.abs(item.amount), 0);
        const totalLiabilities = liabilities.reduce((sum, item) => sum + Math.abs(item.amount), 0);
        const totalEquity = equity.reduce((sum, item) => sum + Math.abs(item.amount), 0);

        // Generate comparison data if requested
        let comparisonData = null;
        if (comparePeriod) {
            comparisonData = generateComparisonData(assets, liabilities, equity);
        }

        const processedData = {
            asOfDate: date.toLocaleDateString(),
            comparePeriod,
            totalAssets,
            totalLiabilities,
            totalEquity,
            currentData: {
                assets: assets.sort((a, b) => a.code.localeCompare(b.code)),
                liabilities: liabilities.sort((a, b) => a.code.localeCompare(b.code)),
                equity: equity.sort((a, b) => a.code.localeCompare(b.code))
            },
            comparisonData,
            isBalanced: Math.abs(totalAssets - (totalLiabilities + totalEquity)) < 0.01
        };

        console.log('Processed Balance Sheet data:', processedData);
        setBalanceSheet(processedData);

        // Show success message
        const balanceStatus = processedData.isBalanced ? 'balanced' : 'unbalanced';
        toast.current.show({
            severity: processedData.isBalanced ? 'success' : 'warn',
            summary: 'Success',
            detail: `Balance sheet generated (${balanceStatus}) with ${assets.length} assets, ${liabilities.length} liabilities, and ${equity.length} equity accounts`,
            life: 3000
        });
    };

    const generateComparisonData = (assets, liabilities, equity) => {
        // Generate mock comparison data for demonstration
        // In a full implementation, you would fetch historical data for the comparison period
        const generateComparisonItem = (item) => {
            const variance = Math.random() * 0.2 - 0.1; // -10% to +10%
            const previousAmount = Math.round(item.amount * (1 + variance));
            return {
                ...item,
                previousAmount,
                change: item.amount - previousAmount,
                changePercent: previousAmount !== 0 ? ((item.amount - previousAmount) / Math.abs(previousAmount)) * 100 : 100
            };
        };

        return {
            assets: assets.map(generateComparisonItem),
            liabilities: liabilities.map(generateComparisonItem),
            equity: equity.map(generateComparisonItem)
        };
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(value);
    };

    const amountTemplate = (rowData) => {
        const amount = Math.abs(rowData.amount);
        const className = rowData.amount < 0 ? 'text-danger' : '';
        return <span className={className}>{formatCurrency(amount)}</span>;
    };

    const compareAmountTemplate = (rowData) => {
        if (!comparePeriod || !rowData.previousAmount) return null;
        const amount = Math.abs(rowData.previousAmount);
        const className = rowData.previousAmount < 0 ? 'text-danger' : '';
        return <span className={className}>{formatCurrency(amount)}</span>;
    };

    const changeTemplate = (rowData) => {
        if (!comparePeriod || rowData.change === undefined) return null;

        const changeClass = rowData.change >= 0 ? 'text-success' : 'text-danger';
        const changeIcon = rowData.change >= 0 ? 'pi pi-arrow-up' : 'pi pi-arrow-down';

        return (
            <span className={changeClass}>
                <i className={`${changeIcon} mr-1`}></i>
                {formatCurrency(Math.abs(rowData.change))} ({Math.abs(rowData.changePercent).toFixed(1)}%)
            </span>
        );
    };

    const generatePDF = () => {
        if (!balanceSheet) {
            toast.current.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'No data available to export'
            });
            return;
        }

        try {
            const doc = new jsPDF();

            // Add title
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text('Balance Sheet', 105, 20, { align: 'center' });

            // Add date
            doc.setFontSize(12);
            doc.setFont('helvetica', 'normal');
            doc.text(`As of ${balanceSheet.asOfDate}`, 105, 30, { align: 'center' });

            // Add balance status
            doc.setFontSize(10);
            const balanceText = balanceSheet.isBalanced ? 'BALANCED' : 'UNBALANCED';
            const balanceColor = balanceSheet.isBalanced ? [0, 128, 0] : [255, 0, 0];
            doc.setTextColor(...balanceColor);
            doc.text(balanceText, 105, 38, { align: 'center' });
            doc.setTextColor(0, 0, 0); // Reset to black

            let yPosition = 50;

            // Assets section
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text('ASSETS', 20, yPosition);
            yPosition += 10;

            const assetsTableData = balanceSheet.currentData.assets.map(item => [
                item.code,
                item.name,
                formatCurrency(Math.abs(item.amount))
            ]);

            doc.autoTable({
                head: [['Code', 'Account Name', 'Amount']],
                body: assetsTableData,
                startY: yPosition,
                theme: 'grid',
                headStyles: {
                    fillColor: [54, 162, 235],
                    textColor: 255,
                    fontStyle: 'bold'
                },
                styles: {
                    fontSize: 10,
                    cellPadding: 3
                }
            });

            yPosition = doc.lastAutoTable.finalY + 5;
            doc.setFont('helvetica', 'bold');
            doc.text(`Total Assets: ${formatCurrency(balanceSheet.totalAssets)}`, 20, yPosition);
            yPosition += 15;

            // Liabilities section
            doc.setFontSize(14);
            doc.text('LIABILITIES', 20, yPosition);
            yPosition += 10;

            const liabilitiesTableData = balanceSheet.currentData.liabilities.map(item => [
                item.code,
                item.name,
                formatCurrency(Math.abs(item.amount))
            ]);

            doc.autoTable({
                head: [['Code', 'Account Name', 'Amount']],
                body: liabilitiesTableData,
                startY: yPosition,
                theme: 'grid',
                headStyles: {
                    fillColor: [255, 99, 132],
                    textColor: 255,
                    fontStyle: 'bold'
                },
                styles: {
                    fontSize: 10,
                    cellPadding: 3
                }
            });

            yPosition = doc.lastAutoTable.finalY + 5;
            doc.text(`Total Liabilities: ${formatCurrency(balanceSheet.totalLiabilities)}`, 20, yPosition);
            yPosition += 15;

            // Equity section
            doc.setFontSize(14);
            doc.text('EQUITY', 20, yPosition);
            yPosition += 10;

            const equityTableData = balanceSheet.currentData.equity.map(item => [
                item.code,
                item.name,
                formatCurrency(Math.abs(item.amount))
            ]);

            doc.autoTable({
                head: [['Code', 'Account Name', 'Amount']],
                body: equityTableData,
                startY: yPosition,
                theme: 'grid',
                headStyles: {
                    fillColor: [75, 192, 192],
                    textColor: 255,
                    fontStyle: 'bold'
                },
                styles: {
                    fontSize: 10,
                    cellPadding: 3
                }
            });

            yPosition = doc.lastAutoTable.finalY + 5;
            doc.text(`Total Equity: ${formatCurrency(balanceSheet.totalEquity)}`, 20, yPosition);
            yPosition += 10;

            // Total Liabilities & Equity
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            const totalLibEquity = balanceSheet.totalLiabilities + balanceSheet.totalEquity;
            doc.text(`Total Liabilities & Equity: ${formatCurrency(totalLibEquity)}`, 20, yPosition);

            // Save the PDF
            const fileName = `balance-sheet-${date ? date.toISOString().split('T')[0] : 'current'}.pdf`;
            doc.save(fileName);

            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: 'PDF exported successfully'
            });

        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to generate PDF: ' + error.message
            });
        }
    };

    const handleExport = () => {
        generatePDF();
    };

    const renderHeader = (title) => {
        return (
            <div className="d-flex justify-content-between align-items-center">
                <h5 className="m-0">{title}</h5>
            </div>
        );
    };

    return (
        <div className="balance-sheet-report">
            <Toast ref={toast} />

            <div className="card mb-4">
                <div className="card-body">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                        <h4 className="mb-3 mb-md-0">Balance Sheet</h4>

                        <div className="d-flex flex-wrap gap-2">
                            <div className="p-field">
                                <label htmlFor="balance-date" className="font-weight-bold mr-2">As of:</label>
                                <Calendar
                                    id="balance-date"
                                    value={date}
                                    onChange={(e) => setDate(e.value)}
                                    showIcon
                                    className="w-auto"
                                />
                            </div>

                            <div className="p-field">
                                <label htmlFor="compare" className="font-weight-bold mr-2">Compare:</label>
                                <Dropdown
                                    id="compare"
                                    value={comparePeriod}
                                    options={compareOptions}
                                    onChange={(e) => setComparePeriod(e.value)}
                                    className="w-auto"
                                />
                            </div>

                            <Button
                                label="Refresh"
                                icon="pi pi-refresh"
                                onClick={fetchBalanceSheet}
                                className="ml-2"
                            />

                            <Button
                                label="Export PDF"
                                icon="pi pi-file-pdf"
                                onClick={handleExport}
                                className="ml-2 p-button-success"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center p-4">
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                            <p className="mt-3">Loading balance sheet data...</p>
                        </div>
                    ) : balanceSheet ? (
                        <div>
                            <div className="text-center mb-4">
                                <h5>Balance Sheet</h5>
                                <p className="text-muted mb-0">As of {balanceSheet.asOfDate}</p>
                                {comparePeriod && (
                                    <p className="text-muted">Compared to Previous {comparePeriod.charAt(0).toUpperCase() + comparePeriod.slice(1)}</p>
                                )}
                                {balanceSheet.isBalanced ? (
                                    <div className="badge bg-success mt-2">Balanced</div>
                                ) : (
                                    <div className="badge bg-warning mt-2">Unbalanced</div>
                                )}
                            </div>

                            {/* Summary Cards */}
                            <div className="row mb-4">
                                <div className="col-md-4 mb-3">
                                    <Card className="h-100 shadow-sm">
                                        <div className="text-center">
                                            <h6 className="text-muted">Total Assets</h6>
                                            <h3 className="text-primary">{formatCurrency(balanceSheet.totalAssets)}</h3>
                                        </div>
                                    </Card>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <Card className="h-100 shadow-sm">
                                        <div className="text-center">
                                            <h6 className="text-muted">Total Liabilities</h6>
                                            <h3 className="text-danger">{formatCurrency(balanceSheet.totalLiabilities)}</h3>
                                        </div>
                                    </Card>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <Card className="h-100 shadow-sm">
                                        <div className="text-center">
                                            <h6 className="text-muted">Total Equity</h6>
                                            <h3 className="text-success">{formatCurrency(balanceSheet.totalEquity)}</h3>
                                        </div>
                                    </Card>
                                </div>
                            </div>

                            {/* Assets Table */}
                            <DataTable
                                value={comparePeriod ? balanceSheet.comparisonData.assets : balanceSheet.currentData.assets}
                                header={renderHeader('Assets')}
                                className="mb-4"
                                emptyMessage="No asset accounts found"
                                sortField="code"
                                sortOrder={1}
                            >
                                <Column field="code" header="Code" style={{ width: '10%' }} sortable />
                                <Column field="name" header="Account" style={{ width: '40%' }} sortable />
                                <Column field="amount" header="Current" body={amountTemplate} style={{ width: '20%' }} sortable />
                                {comparePeriod && (
                                    <>
                                        <Column field="previousAmount" header="Previous" body={compareAmountTemplate} style={{ width: '15%' }} />
                                        <Column field="change" header="Change" body={changeTemplate} style={{ width: '15%' }} />
                                    </>
                                )}
                            </DataTable>
                            <div className="text-end mb-4">
                                <strong>Total Assets: {formatCurrency(balanceSheet.totalAssets)}</strong>
                            </div>

                            {/* Liabilities Table */}
                            <DataTable
                                value={comparePeriod ? balanceSheet.comparisonData.liabilities : balanceSheet.currentData.liabilities}
                                header={renderHeader('Liabilities')}
                                className="mb-4"
                                emptyMessage="No liability accounts found"
                                sortField="code"
                                sortOrder={1}
                            >
                                <Column field="code" header="Code" style={{ width: '10%' }} sortable />
                                <Column field="name" header="Account" style={{ width: '40%' }} sortable />
                                <Column field="amount" header="Current" body={amountTemplate} style={{ width: '20%' }} sortable />
                                {comparePeriod && (
                                    <>
                                        <Column field="previousAmount" header="Previous" body={compareAmountTemplate} style={{ width: '15%' }} />
                                        <Column field="change" header="Change" body={changeTemplate} style={{ width: '15%' }} />
                                    </>
                                )}
                            </DataTable>
                            <div className="text-end mb-4">
                                <strong>Total Liabilities: {formatCurrency(balanceSheet.totalLiabilities)}</strong>
                            </div>

                            {/* Equity Table */}
                            <DataTable
                                value={comparePeriod ? balanceSheet.comparisonData.equity : balanceSheet.currentData.equity}
                                header={renderHeader('Equity')}
                                className="mb-4"
                                emptyMessage="No equity accounts found"
                                sortField="code"
                                sortOrder={1}
                            >
                                <Column field="code" header="Code" style={{ width: '10%' }} sortable />
                                <Column field="name" header="Account" style={{ width: '40%' }} sortable />
                                <Column field="amount" header="Current" body={amountTemplate} style={{ width: '20%' }} sortable />
                                {comparePeriod && (
                                    <>
                                        <Column field="previousAmount" header="Previous" body={compareAmountTemplate} style={{ width: '15%' }} />
                                        <Column field="change" header="Change" body={changeTemplate} style={{ width: '15%' }} />
                                    </>
                                )}
                            </DataTable>
                            <div className="text-end mb-4">
                                <strong>Total Equity: {formatCurrency(balanceSheet.totalEquity)}</strong>
                            </div>

                            {/* Balance Check */}
                            <div className={`card ${balanceSheet.isBalanced ? 'bg-light-success' : 'bg-light-warning'} mt-4`}>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-8 offset-md-2">
                                            <table className="table table-borderless">
                                                <tbody>
                                                    <tr>
                                                        <td className="text-end"><strong>Total Assets:</strong></td>
                                                        <td className="text-end">{formatCurrency(balanceSheet.totalAssets)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-end"><strong>Total Liabilities & Equity:</strong></td>
                                                        <td className="text-end">{formatCurrency(balanceSheet.totalLiabilities + balanceSheet.totalEquity)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-end border-top"><strong>Difference:</strong></td>
                                                        <td className={`text-end border-top ${balanceSheet.isBalanced ? 'text-success' : 'text-warning'}`}>
                                                            <strong>{formatCurrency(Math.abs(balanceSheet.totalAssets - (balanceSheet.totalLiabilities + balanceSheet.totalEquity)))}</strong>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center p-4">
                            <p>No balance sheet data available</p>
                            <p className="text-muted">Please ensure you have asset, liability, and equity accounts with balances as of the selected date.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BalanceSheet;